<template>
	<v-dialog v-model="isOpen" hide-overlay transition="fade-transition" content-class="embedded-forms-dialog">
		<div class="embedded-forms-shell">
			<div class="embedded-forms-backdrop" @click="requestClose"></div>
			<v-card class="embedded-forms-host">
				<v-toolbar flat color="primary" dark>
					<v-toolbar-title>{{ title }}</v-toolbar-title>
					<v-spacer></v-spacer>
					<v-chip v-if="showAuthOverlay" small label color="white" text-color="primary">Authenticating...</v-chip>
					<v-progress-circular v-else-if="!isReady" indeterminate color="white" size="20" width="2" />
					<v-btn icon dark @click="requestClose"><v-icon>close</v-icon></v-btn>
				</v-toolbar>

				<div class="embedded-forms-frame-wrap">
					<div v-if="!isReady || showAuthOverlay" class="embedded-forms-overlay">
						<v-progress-circular indeterminate color="primary" size="36" width="3" class="mb-3" />
						<div class="subheading">{{ overlayText }}</div>
					</div>

					<iframe
						ref="iframe"
						:src="iframeUrl"
						class="embedded-forms-iframe"
						frameborder="0"
						allow="clipboard-read; clipboard-write"
					/>
				</div>
			</v-card>
		</div>
	</v-dialog>
</template>

<script>
import { buildEmbeddedFormsUrl, getFormsAppOrigin, isTrustedFormsMessage } from '@/common/formsEmbed';

export default {
	name: 'EmbeddedFormsAppDialog',
	props: {
		dialog: {
			type: Boolean,
			default: false
		},
		title: {
			type: String,
			default: 'Forms'
		},
		path: {
			type: String,
			required: true
		},
		query: {
			type: Object,
			default: () => ({})
		}
	},
	data() {
		return {
			isOpen: this.dialog,
			isReady: false,
			showAuthOverlay: false,
			lastRoute: null
		};
	},
	computed: {
		iframeUrl() {
			return buildEmbeddedFormsUrl(this.path, this.query);
		},
		overlayText() {
			if (this.showAuthOverlay) return 'Signing you in to forms...';
			return 'Loading forms...';
		}
	},
	watch: {
		dialog(value) {
			this.isOpen = value;
			if (value) {
				this.resetState();
			}
		},
		isOpen(value) {
			if (!value) {
				this.$emit('close');
			}
		},
		iframeUrl() {
			this.resetState();
		}
	},
	created() {
		window.addEventListener('message', this.handleMessage);
	},
	beforeDestroy() {
		window.removeEventListener('message', this.handleMessage);
	},
	methods: {
		resetState() {
			this.isReady = false;
			this.showAuthOverlay = false;
			this.lastRoute = null;
		},
		handleMessage(event) {
			if (!isTrustedFormsMessage(event, getFormsAppOrigin())) return;

			const { type, payload } = event.data;

			switch (type) {
				case 'forms.app.ready':
					this.isReady = true;
					this.showAuthOverlay = false;
					this.lastRoute = payload || null;
					this.$emit('ready', payload);
					break;
				case 'forms.route.changed':
					this.lastRoute = payload || null;
					this.$emit('routeChanged', payload);
					break;
				case 'forms.auth.loginStarted':
					this.showAuthOverlay = true;
					this.$emit('authLoginStarted', payload);
					break;
				case 'forms.submission.completed':
					this.showAuthOverlay = false;
					this.$emit('submissionCompleted', payload);
					break;
				case 'forms.builder.saved':
					this.showAuthOverlay = false;
					this.$emit('builderSaved', payload);
					break;
				case 'forms.builder.published':
					this.showAuthOverlay = false;
					this.$emit('builderPublished', payload);
					break;
				case 'forms.builder.close.requested':
					this.$emit('builderCloseRequested', payload);
					this.requestClose();
					break;
				case 'forms.close.requested':
					this.requestClose();
					break;
				default:
					break;
			}
		},
		requestClose() {
			this.isOpen = false;
		}
	}
};
</script>

<style scoped>
.embedded-forms-shell {
	position: fixed;
	inset: 0;
	display: flex;
	justify-content: flex-end;
	align-items: stretch;
}

.embedded-forms-backdrop {
	position: absolute;
	inset: 0;
	background: rgba(33, 33, 33, 0.46);
}

.embedded-forms-host {
	position: relative;
	width: 72vw;
	max-width: 1100px;
	min-width: 720px;
	height: 100vh;
	display: flex;
	flex-direction: column;
	border-radius: 0;
	box-shadow: -8px 0 24px rgba(0, 0, 0, 0.18);
}

.embedded-forms-frame-wrap {
	position: relative;
	flex: 1;
	min-height: 0;
}

.embedded-forms-iframe {
	width: 100%;
	height: 100%;
	border: 0;
	background: #fff;
}

.embedded-forms-overlay {
	position: absolute;
	inset: 0;
	background: rgba(255, 255, 255, 0.9);
	display: flex;
	align-items: center;
	justify-content: center;
	flex-direction: column;
	z-index: 1;
}

@media (max-width: 1024px) {
	.embedded-forms-host {
		width: 100vw;
		max-width: 100vw;
		min-width: 0;
	}
}
</style>
